import { Select } from '@tempo-io/sui-select';
import type { ReactNode } from 'react';
import { useCallback } from 'react';
import { ImputedBadge } from '@/components/common/items-data-table/cells/ImputedBadge';
import { SettingsGroupLabel } from './SettingsLink';
import type { EstimationGroup, EstimationOption } from './useGroupedEstimationOptions';
import {
  ESTIMATION_MENU_WIDTH,
  filterEstimationOption,
  formatGroupLabel,
  isSettingsOption,
} from './useGroupedEstimationOptions';

interface GroupedEstimationSelectProps {
  value: EstimationOption | null;
  onChange: (option: EstimationOption | null) => void;
  groupedOptions: EstimationGroup[];
  isLoading?: boolean;
  isImputed?: boolean;
  isDisabled?: boolean;
  isClearable?: boolean;
  id?: string;
  name?: string;
  className?: string;
  placeholder?: ReactNode;
  inlineEdit?: boolean;
  cellEdit?: boolean;
  height?: number;
  menuPlacement?: 'auto' | 'bottom' | 'top';
  menuPortalTarget?: HTMLElement | null;
  menuPosition?: 'absolute' | 'fixed';
  onMenuOpen?: () => void;
  onMenuClose?: () => void;
  autoFocus?: boolean;
  openMenuOnFocus?: boolean;
}

export const GroupedEstimationSelect = ({
  value,
  onChange,
  groupedOptions,
  isLoading,
  isImputed,
  isDisabled,
  isClearable = true,
  id,
  name,
  className,
  placeholder,
  inlineEdit,
  cellEdit,
  height,
  menuPlacement,
  menuPortalTarget,
  menuPosition,
  onMenuOpen,
  onMenuClose,
  autoFocus,
  openMenuOnFocus,
}: GroupedEstimationSelectProps) => {
  const formatOptionLabel = useCallback(
    (option: EstimationOption, context: { context: 'menu' | 'value' }) => {
      if (isSettingsOption(option)) {
        return <SettingsGroupLabel />;
      }
      if (context.context === 'value') {
        if (isImputed) {
          return (
            <div className="flex items-center gap-2 w-full">
              <ImputedBadge />
              <span>{option.label}</span>
            </div>
          );
        }
        return option.label;
      }
      return (
        <div className="flex items-center justify-between gap-2 w-full">
          <span>{option.label}</span>
          {option.effortRange && (
            <span className="text-text-neutral-secondary text-xs">{option.effortRange}</span>
          )}
        </div>
      );
    },
    [isImputed],
  );

  return (
    <Select<EstimationOption, false, EstimationGroup>
      id={id}
      name={name}
      className={className}
      isClearable={isClearable}
      inlineEdit={inlineEdit}
      cellEdit={cellEdit}
      height={height}
      menuPortalTarget={menuPortalTarget}
      menuPlacement={menuPlacement}
      menuPosition={menuPosition}
      autoFocus={autoFocus}
      openMenuOnFocus={openMenuOnFocus}
      value={value}
      onChange={onChange}
      onMenuOpen={onMenuOpen}
      onMenuClose={onMenuClose}
      options={groupedOptions}
      isLoading={isLoading}
      isDisabled={isDisabled}
      placeholder={placeholder}
      menuWidth={ESTIMATION_MENU_WIDTH}
      formatGroupLabel={formatGroupLabel}
      filterOption={filterEstimationOption}
      isOptionDisabled={isSettingsOption}
      formatOptionLabel={formatOptionLabel}
      closeMenuOnScroll={(e: Event) =>
        e.target instanceof HTMLElement && !e.target.closest('[class*="-menu"]')
      }
    />
  );
};
